import React from 'react';
import { Moon, Sun, Bell, Menu } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { DownloadsSidebar } from '../ui/DownloadsSidebar';
import { WarehouseSwitcher } from '../warehouses/WarehouseSwitcher';
import {
  activityPanelClosed,
  activityPanelOpened,
  notificationDismissed,
  notificationsCleared,
  selectHasOpenJobs,
  selectIsActivityPanelOpen,
  selectIsSocketConnected,
  selectNotifications,
  selectUnreadCount,
} from '../../features/notifications/notificationsSlice';

const pageInfo = {
  '/dashboard': { title: 'Torre de Control', subtitle: 'Monitorea todas tus operaciones y envíos en tiempo real' },
  '/deliveries': { title: 'Mis Deliveries', subtitle: 'Visualiza, filtra y gestiona todas las órdenes de despacho' },
  '/deliveries/attention': { title: 'Necesitan tu atención', subtitle: 'Los envíos detenidos o sin número de seguimiento' },
  '/pickups': { title: 'My Pickups', subtitle: 'Revisa las órdenes retiradas del centro de distribución y sus etiquetas' },
  '/upload': { title: 'Carga de Órdenes', subtitle: 'Importa envíos masivamente o crea órdenes manualmente' },
  '/account': { title: 'Mi Cuenta', subtitle: 'Configura tus datos de perfil, accesos y preferencias' },
  '/pickup-requests/create': { title: 'Crear solicitud de retiro', subtitle: 'Selecciona el courier con el que quieres agendar un retiro' },
  '/pickup-requests/assign': { title: 'Asignar a manifiesto', subtitle: 'Asocia pedidos a solicitudes de retiro y genera el manifiesto de entrega' },
  '/incoming-upsells': { title: 'Mis Deliveries', subtitle: 'Los envíos que otros partners te ceden' },
  '/sellers': { title: 'Panel de Control', subtitle: 'Configuración global y administración de la plataforma' },
  '/partnerships': { title: 'Panel de Control', subtitle: 'Configuración global y administración de la plataforma' },
  '/my-providers-credentials': { title: 'Panel de Control', subtitle: 'Configuración global y administración de la plataforma' },
  '/external-integrations': { title: 'Panel de Control', subtitle: 'Configuración global y administración de la plataforma' },
  '/shipping-zones': { title: 'Panel de Control', subtitle: 'Configuración global y administración de la plataforma' },
  '/admins': { title: 'Panel de Control', subtitle: 'Configuración global y administración de la plataforma' }
};

/**
 * @param {object} props
 * @param {() => void} [props.onOpenMenu] - Opens the sidebar drawer. Only shown
 *   below `md`, where the sidebar is off screen.
 */
export const Header = ({ onOpenMenu }) => {
  const location = useLocation();
  const dispatch = useDispatch();

  // The panel itself lives in `components/ui/`, which may not import from
  // `features/`, so the store is read here and handed down as props. Whether
  // it is open lives in the store too, so a toast's "Ver" can open it.
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);
  const isSidebarOpen = useSelector(selectIsActivityPanelOpen);

  // An open job is waiting for the socket to say how it ended: if the socket
  // is down, that news is going to be late, and the user should know why.
  const hasOpenJobs = useSelector(selectHasOpenJobs);
  const isSocketConnected = useSelector(selectIsSocketConnected);
  const isSocketMissing = hasOpenJobs && !isSocketConnected;

  const openSidebar = () => dispatch(activityPanelOpened());

  let info = pageInfo[location.pathname];

  // Listings and details that carry their own page header: the bar only names
  // the area, as `/sellers` does for the admin.
  if (
    !info &&
    (location.pathname.startsWith('/sellers/') ||
      location.pathname.startsWith('/shipping-zones/') ||
      location.pathname.startsWith('/admins/') ||
      location.pathname.startsWith('/configuration/'))
  ) {
    info = { title: 'Panel de Control', subtitle: 'Configuración global y administración de la plataforma' };
  }

  // Inside the warehouse module the bar also carries the warehouse in use: it
  // is the context of every screen there, so it is chosen here, once, rather
  // than on each of them.
  const inWarehouse = location.pathname.startsWith('/warehouse/');

  if (!info && inWarehouse) {
    info = { title: 'Bodega', subtitle: 'El inventario y la operación de tus bodegas' };
  }

  if (!info) {
    info = { title: 'NinjaHubs', subtitle: 'Plataforma de Logistica' };
  }

  const toggleDarkMode = () => {
    const isDark = document.documentElement.classList.toggle('dark');
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
  };

  return (
    <>
      <header className="bg-white dark:bg-[#1a1a1a] px-4 sm:px-8 py-4 sm:py-5 border-b border-border flex flex-wrap justify-between items-center gap-3 sticky top-0 z-40">
        <div className="flex items-center gap-3 min-w-0">
          <button
            type="button"
            onClick={onOpenMenu}
            aria-label="Abrir el menú"
            data-testid="open-menu"
            className="md:hidden w-10 h-10 rounded-xl bg-ninja-gray dark:bg-[#2d2d2d] border border-border flex items-center justify-center text-text-secondary flex-shrink-0"
          >
            <Menu size={18} />
          </button>
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold text-ninja-dark dark:text-white">{info.title}</h1>
            <p className="text-sm text-text-secondary mt-1">{info.subtitle}</p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {inWarehouse && <WarehouseSwitcher />}

          <button
            onClick={toggleDarkMode}
            className="w-10 h-10 rounded-xl bg-ninja-gray dark:bg-[#2d2d2d] border border-border cursor-pointer flex items-center justify-center text-text-secondary hover:bg-ninja-green hover:text-white dark:hover:bg-ninja-green transition-all"
            title="Alternar modo oscuro"
          >
            <Moon size={18} className="dark:hidden" />
            <Sun size={18} className="hidden dark:block" />
          </button>

          {/* The badge is content of the button and beats `title` as the
              accessible name: with one notice, a reader announced "1". */}
          <button
            onClick={openSidebar}
            className="relative w-10 h-10 rounded-xl bg-ninja-gray dark:bg-[#2d2d2d] border border-border cursor-pointer flex items-center justify-center text-text-secondary hover:bg-ninja-green hover:text-white dark:hover:bg-ninja-green transition-all"
            title="Centro de Notificaciones y Descargas"
            aria-label={
              unreadCount > 0
                ? `Centro de Notificaciones y Descargas, ${unreadCount} sin leer`
                : 'Centro de Notificaciones y Descargas'
            }
          >
            <Bell size={18} />
            {unreadCount > 0 && (
              <span
                aria-hidden="true"
                className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-warning border-2 border-white dark:border-[#2d2d2d] text-[10px] font-bold text-white flex items-center justify-center"
              >
                {unreadCount > 9 ? '9+' : unreadCount}
              </span>
            )}
            {isSocketMissing && (
              <span
                data-testid="socket-offline-indicator"
                title="Sin conexión en tiempo real: los avisos de tus procesos pueden llegar tarde."
                className="absolute -bottom-1 -right-1 w-3 h-3 rounded-full bg-danger border-2 border-white dark:border-[#2d2d2d]"
              />
            )}
          </button>
        </div>
      </header>

      <DownloadsSidebar
        isOpen={isSidebarOpen}
        onClose={() => dispatch(activityPanelClosed())}
        items={notifications}
        isSocketMissing={isSocketMissing}
        onClear={() => dispatch(notificationsCleared())}
        onDismiss={(id) => dispatch(notificationDismissed(id))}
      />
    </>
  );
};
